import React, {
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';
import { UserDataContext } from '../context/UserDataContext';
import { AuthContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { UserRecord } from '../model/types';
import { useSupabaseListener } from '../hooks/useSupabaseListener';

/**
 * Provides the current user's Firestore user record via Supabase.
 * User creation is handled by the handle_new_auth_user DB trigger —
 * this provider only reads and subscribes to the existing row.
 */
export const UserDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const fetchUser = useCallback(async (): Promise<UserRecord[]> => {
    if (!user) return [];
    const { data, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', user.id)
      .single();
    if (fetchError) {
      if (fetchError.code !== 'PGRST116') {
        // PGRST116 = "no rows" — normal when DB trigger hasn't fired yet
        console.warn('UserDataProvider fetch error:', fetchError.message);
      }
      return [];
    }
    return data ? [data as UserRecord] : [];
  }, [user]);

  const { data, isLoading } = useSupabaseListener<UserRecord>(
    {
      table: 'users',
      filter: user ? `auth_id=eq.${user.id}` : undefined,
      fetchInitial: fetchUser,
      disabled: !user,
    },
    [user?.id],
  );

  const userRecord = data.length > 0 ? data[0] : null;

  return (
    <UserDataContext.Provider
      value={{
        userRecord,
        isLoading,
        error,
        clearError,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};
